import type { DatabaseMaterial, MaterialCategory } from '@/hooks/useMaterials';
import {
  BOARD_TAG_LABELS, BOARD_TAG_ORDER, boardTags,
  INSULATION_TYPE, NOMINAL_WIDTHS, PROFILE_COATING,
  SHEET_THICKNESS_OPTIONS, nominalWidth, profileCoating, resolve,
} from './taxonomy';

/**
 * Faccette di filtro dei materiali, per categoria.
 *
 * Vivono qui e non dentro un componente perche' le usano DUE schermate: il
 * picker del configuratore e la pagina Materiali. Averle in un posto solo e'
 * la stessa ragione per cui esiste `taxonomy.ts` — la versione precedente
 * aveva i filtri scritti a mano nel picker con delle regex, e la pagina
 * Materiali non ne aveva affatto.
 *
 * Dentro una faccetta la scelta e' multipla (OR), fra faccette vale
 * l'intersezione (AND). I vocabolari vengono da `taxonomy.ts`: qui non si
 * scrive nessun elenco di valori.
 */
export interface Facet {
  key: string;
  label: string;
  options: { value: string; label: string }[];
  /** Valori della faccetta per un materiale; vuoto = non classificato. */
  valuesOf: (m: DatabaseMaterial) => string[];
}

const FACET_BOARD_PROPERTIES: Facet = {
  key: 'proprieta',
  label: 'Proprietà',
  options: BOARD_TAG_ORDER.map(t => ({ value: t, label: BOARD_TAG_LABELS[t] })),
  // Derivate da en_520_type (il codice normativo) + tipologia + tipo materiale:
  // una lastra puo' portarne piu' d'una, ed e' giusto che compaia sotto ognuna.
  valuesOf: m => Array.from(boardTags(m)),
};

const FACET_COATING: Facet = {
  key: 'rivestimento',
  label: 'Rivestimento',
  options: PROFILE_COATING.map(o => ({ value: o.value, label: o.label })),
  valuesOf: m => {
    const c = profileCoating(m);
    return c ? [c] : [];
  },
};

const FACET_SHEET_THICKNESS: Facet = {
  key: 'lamierino',
  label: 'Lamierino',
  options: SHEET_THICKNESS_OPTIONS.map(o => ({ value: String(o.value), label: o.label })),
  valuesOf: m => (m.sheet_thickness != null ? [String(Number(m.sheet_thickness))] : []),
};

const FACET_WIDTH: Facet = {
  key: 'larghezza',
  label: 'Larghezza',
  options: [
    ...NOMINAL_WIDTHS.map(w => ({ value: String(w), label: `${w} mm` })),
    { value: 'altre', label: 'Altre' },
  ],
  // Le misure si accorpano: montante 48, 49 e guida 50 stanno tutti sotto "50".
  valuesOf: m => {
    if (m.width == null) return [];
    const n = nominalWidth(m.width);
    return [n != null ? String(n) : 'altre'];
  },
};

const FACET_INSULATION_TYPE: Facet = {
  key: 'tipo',
  label: 'Tipo',
  options: INSULATION_TYPE.map(o => ({ value: o.value, label: o.label })),
  valuesOf: m => {
    const t = resolve(m.material_type, INSULATION_TYPE);
    return t ? [t] : [];
  },
};

/**
 * Faccetta trasversale: cosa manca per rendere il materiale filtrabile.
 * Serve a lavorare in blocco sui materiali incompleti, che oggi sono
 * 152 profili senza lamierino, 104 senza larghezza e i profili senza
 * rivestimento dichiarato.
 */
const FACET_COMPLETENESS: Facet = {
  key: 'completezza',
  label: 'Da completare',
  options: [
    { value: 'larghezza', label: 'Manca larghezza' },
    { value: 'lamierino', label: 'Manca lamierino' },
    { value: 'rivestimento', label: 'Manca rivestimento' },
  ],
  valuesOf: m => {
    const out: string[] = [];
    if (m.width == null) out.push('larghezza');
    if (m.sheet_thickness == null) out.push('lamierino');
    if (profileCoating(m) == null) out.push('rivestimento');
    return out;
  },
};

const PROFILE_FACETS = [FACET_COATING, FACET_SHEET_THICKNESS, FACET_WIDTH, FACET_COMPLETENESS];

export const FACETS_BY_CATEGORY: Partial<Record<MaterialCategory, Facet[]>> = {
  board: [FACET_BOARD_PROPERTIES],
  structure_frame: PROFILE_FACETS,
  structure_guide: PROFILE_FACETS,
  insulation: [FACET_INSULATION_TYPE],
};

export function facetsFor(category: MaterialCategory | 'all' | string): Facet[] {
  if (category === 'all') return [];
  return FACETS_BY_CATEGORY[category as MaterialCategory] ?? [];
}

/**
 * Un materiale passa una faccetta se porta almeno uno dei valori scelti.
 * `selection` e' la mappa faccetta → valori selezionati.
 */
export function matchesFacets(
  m: DatabaseMaterial,
  facets: Facet[],
  selection: Record<string, string[]>,
  skipFacetKey?: string,
): boolean {
  for (const facet of facets) {
    if (facet.key === skipFacetKey) continue;
    const selected = selection[facet.key];
    if (!selected || selected.length === 0) continue;
    if (!facet.valuesOf(m).some(v => selected.includes(v))) return false;
  }
  return true;
}
